"use client"

import React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Handle, Position, useNodes, useEdges } from "reactflow"
import { router } from "@inertiajs/react"
import { Briefcase, Check, ChevronDown, Minus, Plus, Trash2 } from "lucide-react"
import "./Node.css"
import "./UpdateJobNode.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import CopyPasteButtons from "./CopyPasteButtons"
import EnhancedVariableReference from "./EnhancedVariableReference"
import { collectUpstreamFormFieldVariables, hasUpstreamTaskNode } from "@/lib/workflowFormFieldVariables"
import { getSystemVariablesForContext } from "@/lib/workflowSystemVariables"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"

const ensureString = (value) => (value === null || value === undefined ? "" : String(value))
const getSettingValue = (source, key, fallback = "") => {
  if (source?.[key] !== undefined && source?.[key] !== null) {
    return ensureString(source[key])
  }
  return ensureString(fallback)
}

const LP_BASE_URL = "https://api.integrityprodserver.com/intserver-api"
const JOB_STATUS_SQL = "SELECT * FROM jbs_JobStatus WHERE Active = '1'"
const JOB_STATUS_ENDPOINT = `${LP_BASE_URL}/lp/customReport.php?rptSQL=${encodeURIComponent(JOB_STATUS_SQL)}`

const VAR_HINT = "Tip: use {{variableName}} to pull values from previous steps."

const JOB_FIELD_OPTIONS = [
  { value: "update_status", label: "Update status" },
]

const LABEL_KEYS = ["descr", "jobstatus", "status", "name"]

const readLpStatusId = (row) => {
  if (!row || typeof row !== "object") return ""
  const raw = row.Id ?? row.ID ?? ""
  return ensureString(raw)
}

const readLpStatusLabel = (row) => {
  if (!row || typeof row !== "object") return ""
  const raw = row.Descr ?? row.descr ?? row.Description ?? row.description ?? ""
  return ensureString(raw)
}

const readLpStatusIdpkey = (row) => {
  if (!row || typeof row !== "object") return ""
  return ensureString(row.idpkey ?? row.Idpkey ?? row.IDPKey ?? "")
}

const normalizeKey = (key) => ensureString(key).toLowerCase().replace(/[^a-z0-9]/g, "")

const pickField = (row, candidates) => {
  if (!row || typeof row !== "object") return ""
  const normalizedEntries = Object.entries(row).map(([key, value]) => [normalizeKey(key), value])
  for (const candidate of candidates) {
    const match = normalizedEntries.find(([key]) => key === normalizeKey(candidate))
    if (match && match[1] !== null && match[1] !== undefined && ensureString(match[1]) !== "") {
      return ensureString(match[1])
    }
  }
  return ""
}

export const normalizeJobStatusRow = (row) => {
  const statusCode = readLpStatusId(row)
  const label = readLpStatusLabel(row) || pickField(row, LABEL_KEYS)
  const idpkey = readLpStatusIdpkey(row)
  if (!statusCode && !label) return null

  const displayLabel = label || statusCode
  const searchText = [statusCode, displayLabel, idpkey].filter(Boolean).join(" ").toLowerCase()

  return {
    statusCode,
    label: displayLabel,
    idpkey,
    searchText,
  }
}

export const extractJobStatusRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.rows)) return payload.rows
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.report)) return payload.report
  return []
}

export const formatJobStatusDisplay = (status) => {
  if (!status) return "Select status"
  const code = status.statusCode ?? status.jbs_id ?? ""
  const base = status.label || code
  if (code && base && base !== code) {
    return `${base} (${code})`
  }
  return base || code
}

/** Always persist jbs_JobStatus.Id — never the numeric idpkey. */
export const resolveJobStatusCode = (value, statusOptions = []) => {
  const normalized = ensureString(value)
  if (!normalized) return ""
  if (statusOptions.some((status) => status.statusCode === normalized)) {
    return normalized
  }
  const matchedByIdpkey = statusOptions.find((status) => status.idpkey === normalized)
  return matchedByIdpkey?.statusCode || normalized
}

const UpdateJobNode = ({ data, id, databaseId }) => {
  const { currentWorkflow, updateNode } = useWorkflow()
  const { expanded = true } = data
  const settings = data?.settings || {}
  const resolvedWorkflowId = currentWorkflow?.id ?? data?.workflowId ?? null
  const stepDatabaseId =
    databaseId ??
    data?.databaseId ??
    (typeof id === "string" && id.startsWith("step-") ? id.replace("step-", "") : null)

  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [statusOptions, setStatusOptions] = useState([])
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false)
  const [statusLoadError, setStatusLoadError] = useState("")
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
  const legacyMigrationAttemptedRef = useRef(false)
  const pendingSaveRef = useRef(null)
  const statusOptionsRef = useRef([])
  const nodes = useNodes()
  const edges = useEdges()

  const buildState = (src, srcSettings) => ({
    label: ensureString(src.label) || "Update Job",
    name: ensureString(src.name) || ensureString(src.label) || "Update Job",
    description: ensureString(src.description),
    operation: ensureString(src.operation) || getSettingValue(srcSettings, "operation", "update_status") || "update_status",
    jobId: ensureString(src.jobId) || getSettingValue(srcSettings, "job_id", ""),
    jbsId: ensureString(src.jbsId) || getSettingValue(srcSettings, "jbs_id", ""),
    jobStatusLabel: ensureString(src.jobStatusLabel) || getSettingValue(srcSettings, "job_status_label", ""),
    expanded: src.expanded ?? true,
  })

  const [nodeData, setNodeData] = useState(() => buildState(data, settings))
  const nodeDataRef = useRef(nodeData)

  const buildPersistedSettings = useCallback((state, statusOptions = statusOptionsRef.current) => {
    const resolvedJbsId = resolveJobStatusCode(state?.jbsId, statusOptions)
    return {
      operation: state?.operation || "update_status",
      job_id: state?.jobId || "",
      jbs_id: resolvedJbsId,
      job_status_label: state?.jobStatusLabel || "",
    }
  }, [])

  const persistNodeData = useCallback((state) => {
    const resolvedJbsId = resolveJobStatusCode(state.jbsId, statusOptionsRef.current)
    const matched = statusOptionsRef.current.find((status) => status.statusCode === resolvedJbsId)
    const payload = {
      ...state,
      jbsId: resolvedJbsId,
      jobStatusLabel: matched ? formatJobStatusDisplay(matched) : state.jobStatusLabel,
      settings: buildPersistedSettings({ ...state, jbsId: resolvedJbsId }, statusOptionsRef.current),
    }
    pendingSaveRef.current = {
      jbsId: payload.jbsId,
      jobId: payload.jobId,
    }
    setNodeData(payload)
    nodeDataRef.current = payload
    if (currentWorkflow) {
      updateNode(currentWorkflow.id, id, { data: payload })
    }
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, { ...payload, expanded })
    }
  }, [buildPersistedSettings, currentWorkflow, data, expanded, id, updateNode])

  useEffect(() => {
    const incoming = buildState(data, data?.settings || {})

    if (pendingSaveRef.current) {
      const pending = pendingSaveRef.current
      if (incoming.jbsId !== pending.jbsId || incoming.jobId !== pending.jobId) {
        return
      }
      pendingSaveRef.current = null
    }

    setNodeData(incoming)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.label, data.name, data.description, data.operation, data.jobId, data.jbsId,
    data.jobStatusLabel, data.expanded, data.settings, id,
  ])

  useEffect(() => {
    nodeDataRef.current = nodeData
  }, [nodeData])

  useEffect(() => {
    if (nodeData.operation !== "update_status") {
      return undefined
    }

    const abortController = new AbortController()

    const loadStatuses = async () => {
      setIsLoadingStatuses(true)
      setStatusLoadError("")

      try {
        const response = await fetch(JOB_STATUS_ENDPOINT, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: abortController.signal,
        })

        if (!response.ok) {
          throw new Error(`Job status request failed with status ${response.status}`)
        }

        const payload = await response.json()
        const rows = extractJobStatusRows(payload)
        const normalized = rows
          .map((row) => normalizeJobStatusRow(row))
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label))

        if (normalized.length > 0) {
          statusOptionsRef.current = normalized
          setStatusOptions(normalized)
        } else {
          setStatusLoadError("No active job statuses returned from LP API.")
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Failed to load LP job statuses:", error)
          setStatusLoadError("Unable to load job statuses from LP API.")
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingStatuses(false)
        }
      }
    }

    loadStatuses()

    return () => {
      abortController.abort()
    }
  }, [nodeData.operation])

  // Older node saves stored idpkey (e.g. 135) in jbs_id while displaying the LP Id code (e.g. PL).
  useEffect(() => {
    if (legacyMigrationAttemptedRef.current) return
    if (statusOptions.length === 0 || !nodeData.jbsId) return

    const currentValue = ensureString(nodeData.jbsId)
    const matchedByCode = statusOptions.find((status) => status.statusCode === currentValue)
    if (matchedByCode) {
      legacyMigrationAttemptedRef.current = true
      return
    }

    const matchedByIdpkey = statusOptions.find((status) => status.idpkey === currentValue)
    if (!matchedByIdpkey?.statusCode) return

    legacyMigrationAttemptedRef.current = true
    persistNodeData({
      ...nodeDataRef.current,
      jbsId: matchedByIdpkey.statusCode,
      jobStatusLabel: formatJobStatusDisplay(matchedByIdpkey),
    })
  }, [statusOptions, nodeData.jbsId, persistNodeData])

  const handleChange = (field, value, options = {}) => {
    const { persistOnChange = false } = options
    const updated = {
      ...nodeDataRef.current,
      [field]: ensureString(value),
    }
    if (field === "name") {
      updated.label = ensureString(value) || "Update Job"
    }
    nodeDataRef.current = updated
    setNodeData(updated)
    if (persistOnChange) {
      persistNodeData(updated)
    }
  }

  const handleFieldChange = (fieldValue) => {
    const updated = {
      ...nodeDataRef.current,
      operation: ensureString(fieldValue),
    }
    if (fieldValue !== "update_status") {
      updated.jbsId = ""
      updated.jobStatusLabel = ""
    }
    persistNodeData(updated)
  }

  const handleStatusSelect = (selectedStatusCode) => {
    const matched = statusOptions.find((status) => status.statusCode === selectedStatusCode)
    persistNodeData({
      ...nodeDataRef.current,
      jbsId: ensureString(selectedStatusCode),
      jobStatusLabel: matched ? formatJobStatusDisplay(matched) : ensureString(selectedStatusCode),
    })
    setStatusDropdownOpen(false)
  }

  const handleBlur = () => {
    setTimeout(() => {
      const current = nodeDataRef.current
      if (!current) return
      persistNodeData(current)
    }, 0)
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest(".node-content")
      if (!nodeContent) return
      if (!e.target.closest(".update-job-node-outline")) return
      if (e.ctrlKey || e.shiftKey) return
      e.stopPropagation()
    }
    document.addEventListener("wheel", handleWheel, { passive: false, capture: true })
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true })
    }
  }, [id, expanded])

  const baseFieldClassName =
    "w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"

  const fieldClassName =
    `${baseFieldClassName} dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500`

  const variableFieldClassName =
    `${baseFieldClassName} dark:focus-within:outline-none dark:focus-within:ring-2 dark:focus-within:ring-blue-500 [&_.variable-input]:border-transparent [&_.variable-input]:bg-transparent [&_.variable-input]:shadow-none [&_.variable-input]:h-auto [&_.variable-input]:min-h-0 [&_.variable-input]:rounded-none [&_.variable-input]:px-0 [&_.variable-input]:py-0 [&_.variable-input:focus]:!ring-0 [&_.variable-input:focus]:!outline-none [&_.variable-input:focus]:!border-transparent [&_.variable-textarea]:border-transparent [&_.variable-textarea]:bg-transparent [&_.variable-textarea]:shadow-none [&_.variable-textarea]:rounded-none [&_.variable-textarea]:px-0 [&_.variable-textarea]:py-0 [&_.variable-textarea:focus]:!ring-0 [&_.variable-textarea:focus]:!outline-none [&_.variable-textarea:focus]:!border-transparent`

  const preventNodeDrag = (event) => {
    event.stopPropagation()
  }

  const getAvailableRequestVariables = () => {
    if (!nodes || !edges) return []
    const availableVariables = []
    const visitedNodes = new Set()

    const findUpstreamRequestNodes = (nodeId) => {
      if (visitedNodes.has(nodeId)) return
      visitedNodes.add(nodeId)

      const node = nodes.find((n) => n.id === nodeId)
      if (!node) return

      if (
        (node.type === "requestNode" || node.type === "getNoteNode") &&
        Array.isArray(node.data?.extractedVariables)
      ) {
        availableVariables.push(...node.data.extractedVariables)
      }

      const incomingEdges = edges.filter((edge) => edge.target === nodeId)
      incomingEdges.forEach((edge) => findUpstreamRequestNodes(edge.source))
    }

    findUpstreamRequestNodes(id)
    return availableVariables.filter((variable) => variable && typeof variable.name === "string")
  }

  const getAvailableFormFields = () => collectUpstreamFormFieldVariables(nodes, edges, id)

  const getSystemVariables = () =>
    getSystemVariablesForContext({ hasUpstreamCompletedTask: hasUpstreamTaskNode(nodes, edges, id) })

  const getAvailableVariables = () => {
    const requestVariables = getAvailableRequestVariables()
    const formVariables = getAvailableFormFields()
    return [...requestVariables, ...formVariables]
  }

  const selectedStatus = useMemo(() => {
    const byCode = statusOptions.find((status) => status.statusCode === nodeData.jbsId)
    if (byCode) return byCode
    return statusOptions.find((status) => status.idpkey === nodeData.jbsId) ?? null
  }, [statusOptions, nodeData.jbsId])

  const statusDisplayValue = selectedStatus
    ? formatJobStatusDisplay(selectedStatus)
    : (nodeData.jobStatusLabel || (nodeData.jbsId ? `Status ID ${nodeData.jbsId}` : ""))

  const hasStatusSelection = Boolean(statusDisplayValue)
  const showStatusField = nodeData.operation === "update_status"

  return (
    <div
      className={`trigger-node-container update-job-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "update-job-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <Briefcase size={16} className="trigger-icon" />
          <span>{nodeData.name && nodeData.name.trim() !== "" ? nodeData.name : "Update Job"}</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons
            nodeId={id}
            nodeType="updateJobNode"
            nodeName={nodeData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <button
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm("Are you sure you want to delete this node?")) {
                try {
                  if (!resolvedWorkflowId || !stepDatabaseId) {
                    throw new Error("Unable to delete node: missing workflow or step database ID")
                  }
                  router.delete(`/workflows/${resolvedWorkflowId}/steps/${stepDatabaseId}`, {
                    preserveState: true,
                    preserveScroll: true,
                    only: [],
                    onSuccess: () => {
                      if (typeof data.onDeleteNode === "function") {
                        data.onDeleteNode(id)
                      }
                    },
                    onError: (errors) => {
                      console.error("Failed to delete update job node via Inertia:", errors)
                      alert("Failed to delete node. Please try again.")
                    },
                  })
                } catch (error) {
                  console.error("Failed to delete node:", error)
                  alert("Failed to delete node. Please try again.")
                }
              }
            }}
            title="Delete node"
            style={{ color: "#ef4444" }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          className="node-content nowheel nodrag dark:bg-card dark:text-white dark:border-border"
          onWheel={(e) => e.stopPropagation()}
          onMouseDown={preventNodeDrag}
          onPointerDown={preventNodeDrag}
        >
          <div className="node-field nodrag" onMouseDown={preventNodeDrag} onPointerDown={preventNodeDrag}>
            <label className="dark:text-white">Node Name</label>
            <EnhancedVariableReference
              value={nodeData.name}
              onChange={(value) => handleChange("name", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder="Update Job"
              className={variableFieldClassName}
              showDateVariables={true}
            />
          </div>

          <div className="node-field nodrag" onMouseDown={preventNodeDrag} onPointerDown={preventNodeDrag}>
            <label className="dark:text-white">Description</label>
            <EnhancedVariableReference
              value={nodeData.description}
              onChange={(value) => handleChange("description", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder="Update the LP job status"
              className={variableFieldClassName}
              showDateVariables={true}
            />
          </div>

          <div className="node-field nodrag" onMouseDown={preventNodeDrag} onPointerDown={preventNodeDrag}>
            <label className="dark:text-white">
              Job ID<span className="update-job-required">*</span>
            </label>
            <EnhancedVariableReference
              value={nodeData.jobId}
              onChange={(value) => handleChange("jobId", value)}
              onBlur={handleBlur}
              availableVariables={getAvailableVariables()}
              systemVariables={getSystemVariables()}
              placeholder="{{job_id}}"
              className={variableFieldClassName}
              showDateVariables={true}
            />
            <div className="update-job-field-hint">{VAR_HINT}</div>
          </div>

          <div className="node-field nodrag" onMouseDown={preventNodeDrag} onPointerDown={preventNodeDrag}>
            <label className="dark:text-white">
              Job Field<span className="update-job-required">*</span>
            </label>
            <div className="update-job-select-wrapper relative">
              <select
                value={nodeData.operation}
                onChange={(e) => handleFieldChange(e.target.value)}
                onBlur={handleBlur}
                className={cn(fieldClassName, "appearance-none pr-8")}
              >
                {JOB_FIELD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="update-job-field-chevron pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2" />
            </div>
          </div>

          {showStatusField && (
            <div className="node-field nodrag" onMouseDown={preventNodeDrag} onPointerDown={preventNodeDrag}>
              <label className="dark:text-white">
                Status Value<span className="update-job-required">*</span>
              </label>
              <Popover open={statusDropdownOpen} onOpenChange={setStatusDropdownOpen} modal={true}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={statusDropdownOpen}
                    disabled={isLoadingStatuses && statusOptions.length === 0}
                    className={cn(
                      "update-job-status-trigger flex items-center justify-between text-left font-normal",
                      fieldClassName
                    )}
                    onMouseDown={preventNodeDrag}
                    onPointerDown={preventNodeDrag}
                  >
                    <span
                      className={cn(
                        "truncate text-left flex-1 font-normal",
                        !hasStatusSelection && "text-gray-400"
                      )}
                    >
                      {isLoadingStatuses && statusOptions.length === 0
                        ? "Loading statuses..."
                        : (statusDisplayValue || "Select status")}
                    </span>
                    <ChevronDown className="update-job-field-chevron ml-2 h-4 w-4 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0 max-h-[280px] overflow-hidden" align="start">
                  <Command key={statusDropdownOpen ? "status-open" : "status-closed"}>
                    <CommandInput placeholder="Search by name, description, or ID..." />
                    <CommandList>
                      <CommandEmpty>No job statuses found.</CommandEmpty>
                      <CommandGroup>
                        {statusOptions.map((status) => (
                          <CommandItem
                            key={`${status.idpkey || ""}-${status.statusCode}`}
                            keywords={[status.statusCode, status.label, status.idpkey].filter(Boolean)}
                            value={`${status.statusCode} ${status.label} ${status.idpkey}`}
                            onSelect={() => handleStatusSelect(status.statusCode)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0",
                                nodeData.jbsId === status.statusCode ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="truncate">{formatJobStatusDisplay(status)}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {isLoadingStatuses && (
                <div className="update-job-field-hint">Loading active job statuses from LP...</div>
              )}
              {statusLoadError && (
                <div className="update-job-field-hint">{statusLoadError}</div>
              )}
            </div>
          )}
        </div>
      )}

      <Handle type="target" position={Position.Top} id="b" />
      <Handle type="source" position={Position.Bottom} id="a" />
    </div>
  )
}

export default UpdateJobNode
